import { WorkflowPermissions } from '@ply-io/ply-front-shared-utilities';

import { MODULE_NAME } from './const';
import { getWorkflowVersion } from './modules/versions/getters';
import StoreModuleService from '../StoreModuleService';
import { getNode, getNodeTestResult } from '../nodes/getters';
import { editedWorkflowVersion, editWorkflow } from '../selectedWorkflow/getters';
import { getAction } from '@/store/modules/actions/getters';
import { selectedTaskId } from '@/store/modules/analytics/getters.js';
import { user as userGetter } from '@/store/modules/auth/getters';
import { getInstallable } from '@/store/modules/installables/getters';
import { getNativeElement } from '@/store/modules/nativeElements/getters';
import { useWorkflowVersionGraph } from '@/store/modules/treeBlocks/getters';
import { getUiElement } from '@/store/modules/uiElements/getters';
import { getConfiguration, getConfigurationResponse } from '@/store/modules/workflowConfigurations/getters';
import { useSelectedWorkflowVersionId, isVersionHistoryOpen } from '@/store/modules/workflows/modules/versions/getters.js';
import WorkflowService from '@/utils/WorkflowService';
import ElementService from '@/utils/element-service';
import { getSortedGraph } from '@/utils/workflow/workflow-graph-service';
import WorkflowConfigurationService from '@/utils/workflow-configuration-service';

const buildWorkflowGetterObject = (getter) => StoreModuleService.buildGetterObject(MODULE_NAME, getter);

// getters declarations - implementation in store itself
export const data = buildWorkflowGetterObject('data');
export const getWorkflow = buildWorkflowGetterObject('getWorkflow');
export const getWorkflowDraftNodesData = buildWorkflowGetterObject('getWorkflowDraftNodesData');
export const getWorkflowVersionNodesData = buildWorkflowGetterObject('getWorkflowVersionNodesData');

export const getWorkflowDraftNodesDataByUUID = buildWorkflowGetterObject('getWorkflowDraftNodesDataByUUID');
export const getWorkflowDraftVersion = buildWorkflowGetterObject('getWorkflowDraftVersion');
export const getWorkflowCurrentVersion = buildWorkflowGetterObject('getWorkflowCurrentVersion');
export const hasWorkflowEditPermissions = buildWorkflowGetterObject('hasWorkflowEditPermissions');
export const hasWorkflowDeletePermissions = buildWorkflowGetterObject('hasWorkflowDeletePermissions');
export const hasWorkflowEditGeneralConfigurationPermissions = buildWorkflowGetterObject('hasWorkflowEditGeneralConfigurationPermissions');
export const hasWorkflowToggleActivePermissions = buildWorkflowGetterObject('hasWorkflowToggleActivePermissions');
export const hasWorkflowPermissions = buildWorkflowGetterObject('hasWorkflowPermissions');
export const rootNodeId = buildWorkflowGetterObject('rootNodeId');

export const allWorkflows = buildWorkflowGetterObject('allWorkflows');
export const sharedWorkflows = buildWorkflowGetterObject('sharedWorkflows');
export const editFlowLink = buildWorkflowGetterObject('editFlowLink');
export const workflowSharedData = buildWorkflowGetterObject('workflowSharedData');
export const workflowSharedDataLoading = buildWorkflowGetterObject('workflowSharedDataLoading');
export const isWorkflowSharedWithOthers = buildWorkflowGetterObject('isWorkflowSharedWithOthers');
export const getWorkflowInstallable = buildWorkflowGetterObject('getWorkflowInstallable');
export const doesWorkflowHasInstallable = buildWorkflowGetterObject('doesWorkflowHasInstallable');

export const nodesData = buildWorkflowGetterObject('nodesData');
export const nodesDataArray = buildWorkflowGetterObject('nodesDataArray');
export const nodesDataSorted = buildWorkflowGetterObject('nodesDataSorted');
export const rootNode = buildWorkflowGetterObject('rootNode');
export const element = buildWorkflowGetterObject('element');
export const uiElement = buildWorkflowGetterObject('uiElement');
export const nativeElement = buildWorkflowGetterObject('nativeElement');
export const isNativeElement = buildWorkflowGetterObject('isNativeElement');
export const elementContextualData = buildWorkflowGetterObject('elementContextualData');
export const generalSetupMappedFields = buildWorkflowGetterObject('generalSetupMappedFields');
export const userSetupMappedFields = buildWorkflowGetterObject('userSetupMappedFields');
export const rootNodeTestResult = buildWorkflowGetterObject('rootNodeTestResult');

// Configuration related
export const generalConfigurationId = buildWorkflowGetterObject('generalConfigurationId');
export const publishedGeneralConfigurationId = buildWorkflowGetterObject('publishedGeneralConfigurationId');
export const generalConfiguration = buildWorkflowGetterObject('generalConfiguration');
export const userSpecificConfigurationId = buildWorkflowGetterObject('userSpecificConfigurationId');
export const publishedUserSpecificConfigurationId = buildWorkflowGetterObject('publishedUserSpecificConfigurationId');
export const userSpecificConfiguration = buildWorkflowGetterObject('userSpecificConfiguration');
export const generalConfigurationResponse = buildWorkflowGetterObject('generalConfigurationResponse');
export const userSpecificConfigurationResponse = buildWorkflowGetterObject('userSpecificConfigurationResponse');
export const configurationsResponse = buildWorkflowGetterObject('configurationsResponse');
export const getConfigurationFieldResponse = buildWorkflowGetterObject('getConfigurationFieldResponse');
export const useAppsWorkspaces = buildWorkflowGetterObject('useAppsWorkspaces');
export const isWaitingForSampleData = buildWorkflowGetterObject('isWaitingForSampleData');
export const updatingWorkflowIds = buildWorkflowGetterObject('updatingWorkflowIds');
export const canEditWorkflow = buildWorkflowGetterObject('canEditWorkflow');
export const isLoadingWorkflowGraph = buildWorkflowGetterObject('isLoadingWorkflowGraph');

const getters = {
	[data.getter]: (state) => state.data,
	[useAppsWorkspaces.getter]: (state) => state.appsWorkspaces,
	[allWorkflows.getter]: (state, getters) => Object.values(getters[data.getter]),
	[sharedWorkflows.getter]: (state) => state.sharedWorkflows,
	[getWorkflow.getter]: (state) => (workflowId) => state.data[workflowId],
	[workflowSharedData.getter]: (state) => (workflowId) => state.sharedData[workflowId],
	[workflowSharedDataLoading.getter]: (state) => state.sharedDataLoading,
	[isLoadingWorkflowGraph.getter]: (state) => state.isLoadingWorkflowGraph,
	/**
	 * returns workflows draft nodes in dictionary, keys are the pks of the nodes
	 */
	[getWorkflowDraftNodesData.getter]: (state, getters, rootState, rootGetters) =>
		WorkflowService.mapNodesOfWorkflowByField({
			getNode,
			rootGetters,
			fieldToMapBy: 'id',
			workflowVersionFullObject: rootGetters[editedWorkflowVersion.fullPath],
		}),

	/**
	 * returns workflows version nodes in dictionary, keys are the pks of the nodes
	 */
	[getWorkflowVersionNodesData.getter]:
		(state, getters, rootState, rootGetters) =>
		({ workflowVersionId }) =>
			WorkflowService.mapNodesOfWorkflowByField({
				getNode,
				rootGetters,
				fieldToMapBy: 'id',
				workflowVersionFullObject: rootGetters[getWorkflowVersion.fullPath](workflowVersionId),
			}),

	[getWorkflowDraftNodesDataByUUID.getter]:
		(state, getters, rootState, rootGetters) =>
		({ workflowId, workflowVersionId }) => {
			const fieldToMapBy = 'uuid';
			const workflow = getters[getWorkflow.getter](workflowId);
			const isDraftVersion = workflow?.current_draft?.id === workflowVersionId;
			const workflowVersionFullObject =
				!workflowVersionId || isDraftVersion
					? rootGetters[editedWorkflowVersion.fullPath]
					: rootGetters[getWorkflowVersion.fullPath](workflowVersionId);

			return WorkflowService.mapNodesOfWorkflowByField({ getNode, rootGetters, fieldToMapBy, workflowVersionFullObject });
		},
	[getWorkflowDraftVersion.getter]: (state, getters) => (workflowId) => {
		const workflow = getters[getWorkflow.getter](workflowId);
		if (workflow) {
			return workflow.current_draft;
		}
		return null;
	},
	[hasWorkflowEditPermissions.getter]: (state, getters) => (workflowId) =>
		getters[hasWorkflowPermissions.getter](workflowId, WorkflowPermissions.ChangeWorkflow),
	[canEditWorkflow.getter]: (state, getters, rootState, rootGetters) => (workflowId) => {
		// This is a getter used mostly in builder screen, which allows to view also previous workflow versions, which are not editable. NOTICE: use this only if it's builder-related.
		// For permission checks outside the builder area, such as ModuleCard.vue, FeatureDetails.vue, use hasWorkflowEditPermissions instead.
		const isVersionHistoryMenuOpen = rootGetters[isVersionHistoryOpen.fullPath];
		const workflow = getters[getWorkflow.getter](workflowId);
		if (!workflow) {
			return false;
		}
		const draftVersionId = workflow.current_draft?.id;
		const isViewingPreviousVersion = isVersionHistoryMenuOpen
			? rootGetters[useSelectedWorkflowVersionId.fullPath] !== draftVersionId
			: false;
		const isViewingTaskLogRun = !!rootGetters[selectedTaskId.fullPath];
		return !isViewingTaskLogRun && !isViewingPreviousVersion && getters[hasWorkflowEditPermissions.getter](workflowId);
	},
	[hasWorkflowDeletePermissions.getter]: (state, getters) => (workflowId) =>
		getters[hasWorkflowPermissions.getter](workflowId, WorkflowPermissions.DeleteWorkflow),
	[hasWorkflowEditGeneralConfigurationPermissions.getter]: (state, getters) => (workflowId) =>
		getters[hasWorkflowPermissions.getter](workflowId, WorkflowPermissions.ChangeWorkflowGeneralConfiguration),
	[hasWorkflowToggleActivePermissions.getter]: (state, getters) => (workflowId) =>
		getters[hasWorkflowPermissions.getter](workflowId, WorkflowPermissions.ChangeWorkflowActiveState),
	[hasWorkflowPermissions.getter]: (state, getters) => (workflowId, permission) => {
		const wf = getters[getWorkflow.getter](workflowId);
		// these workflow permissions already have computed project permissions inside - regarding the workflow
		if (!wf || !wf.permissions) {
			return false;
		}
		return wf.permissions.includes(permission);
	},
	[editFlowLink.getter]: () => (projectId, flowId) => `/project/${projectId}/features/${flowId}`,
	[doesWorkflowHasInstallable.getter]: (state, getters) => (workflowId) => !!getters[getWorkflow.getter](workflowId).installable,
	[getWorkflowInstallable.getter]: (state, getters, rootState, rootGetters) => (workflowId) =>
		rootGetters[getInstallable.fullPath](getters[getWorkflow.getter](workflowId).installable),
	[getWorkflowCurrentVersion.getter]: (state, getters, rootState, rootGetters) => (workflowId) => {
		const workflow = getters[getWorkflow.getter](workflowId);
		return rootGetters[getWorkflowVersion.fullPath](workflow.current_version);
	},
	[nodesData.getter]:
		(state, getters, rootState, rootGetters) =>
		(workflowId = getters[editWorkflow]?.id, workflowVersionId) => {
			const workflow = getters[getWorkflow.getter](workflowId);
			const isDraftVersion = workflow?.current_draft?.id === workflowVersionId;
			if (!workflowVersionId || isDraftVersion) {
				return rootGetters[getWorkflowDraftNodesData.fullPath];
			}
			return rootGetters[getWorkflowVersionNodesData.fullPath]({ workflowVersionId });
		},
	[nodesDataArray.getter]: (state, getters) => (wfId, workflowVersionId) =>
		Object.values(getters[nodesData.getter](wfId, workflowVersionId)),
	[nodesDataSorted.getter]: (state, getters, rootState, rootGetters) => (workflowId, workflowVersionId) => {
		const workflow = getters[getWorkflow.getter](workflowId);
		if (!workflow) {
			return [];
		}
		if (!workflowVersionId) {
			workflowVersionId = workflow.current_draft?.id;
		}
		const { nodes, edges } = rootGetters[useWorkflowVersionGraph.fullPath](workflowId, workflowVersionId);
		const actionGetter = rootGetters[getAction.fullPath];
		return getSortedGraph({ nodes, edges, actionGetter });
	},
	[rootNode.getter]: (state, getters) => (wfId, workflowVersionId) =>
		getters[nodesDataArray.getter](wfId, workflowVersionId).find(({ id }) => id === getters[rootNodeId.getter](wfId, workflowVersionId)),
	[rootNodeId.getter]: (state, getters, rootState, rootGetters) => (wfId, workflowVersionId) => {
		const workflowDraftVersion = rootGetters[getWorkflowDraftVersion.fullPath](wfId);
		const isVersionCurrentDraft = workflowDraftVersion?.id === workflowVersionId;
		if (isVersionCurrentDraft || !workflowVersionId) {
			return workflowDraftVersion?.root_node;
		}
		return rootGetters[getWorkflowVersion.fullPath](workflowVersionId)?.root_node;
	},
	[element.getter]: (state, getters) => (wfId) =>
		(getters[uiElement.getter] ? getters[uiElement.getter] : getters[nativeElement.getter])(wfId),
	[uiElement.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getUiElement.fullPath](rootGetters[getWorkflowDraftVersion.fullPath](wfId)?.ui_element?.id),
	[nativeElement.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getNativeElement.fullPath](rootGetters[getWorkflow.fullPath](wfId)?.native_element?.id),
	[isNativeElement.getter]: (state, getters) => (wfId) => Boolean(getters[nativeElement.getter](wfId)),
	[elementContextualData.getter]: (state, getters, rootState, rootGetters) => (wfId) => {
		const _element = rootGetters[element.fullPath](wfId);
		if (!_element) {
			return {};
		}
		return ElementService.getElementContextualData(_element);
	},
	[rootNodeTestResult.getter]: (state, getters, rootState, rootGetters) => (wfId) => {
		const rootNodeObject = getters[rootNode.getter](wfId);
		if (!rootNodeObject) {
			return {};
		}
		return rootGetters[getNodeTestResult.fullPath](rootNodeObject.id);
	},

	// Configuration related
	[generalConfigurationId.getter]: (state, getters) => (wfId) => getters[getWorkflowDraftVersion.getter](wfId)?.general_config_options,
	[publishedGeneralConfigurationId.getter]: (state, getters) => (wfId) => getters[getWorkflow.getter](wfId)?.current_version_general_setup,
	[generalConfiguration.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getConfiguration.fullPath](getters[generalConfigurationId.getter](wfId)),

	[userSpecificConfigurationId.getter]: (state, getters) => (wfId) =>
		getters[getWorkflowDraftVersion.getter](wfId)?.user_specific_config_options,
	[publishedUserSpecificConfigurationId.getter]: (state, getters) => (wfId) =>
		getters[getWorkflow.getter](wfId)?.current_version_user_setup,
	[userSpecificConfiguration.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getConfiguration.fullPath](getters[userSpecificConfigurationId.getter](wfId)),

	[userSpecificConfigurationResponse.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getConfigurationResponse.fullPath](getters[userSpecificConfigurationId.getter](wfId)),
	[generalConfigurationResponse.getter]: (state, getters, rootState, rootGetters) => (wfId) =>
		rootGetters[getConfigurationResponse.fullPath](getters[generalConfigurationId.getter](wfId)),
	[configurationsResponse.getter]: (state, getters) => (wfId) => ({
		...(getters[generalConfigurationResponse.getter](wfId) || {}),
		...(getters[userSpecificConfigurationResponse.getter](wfId) || {}),
	}),

	[getConfigurationFieldResponse.getter]: (state, getters) => (wfId) => (fieldUUID) =>
		getters[configurationsResponse.getter](wfId)?.[fieldUUID],

	[generalSetupMappedFields.getter]: (state, getters) => (wfId) => {
		const generalConfigurationGetter = getters[generalConfiguration.getter](wfId);
		const getConfigurationFieldResponseGetter = getters[getConfigurationFieldResponse.getter](wfId);
		return WorkflowConfigurationService.getConfigurationMappedFields(generalConfigurationGetter, getConfigurationFieldResponseGetter);
	},
	[userSetupMappedFields.getter]: (state, getters) => (wfId) => {
		const userSpecificConfigurationGetter = getters[userSpecificConfiguration.getter](wfId);
		const getConfigurationFieldResponseGetter = getters[getConfigurationFieldResponse.getter](wfId);
		return WorkflowConfigurationService.getConfigurationMappedFields(userSpecificConfigurationGetter, getConfigurationFieldResponseGetter);
	},
	[isWorkflowSharedWithOthers.getter]: (state, getters, rootState, rootGetters) => (wfId) => {
		const sharedData = getters[workflowSharedData.getter](wfId);
		if (!sharedData) {
			return undefined;
		}
		const currentUserId = rootGetters[userGetter.fullPath]?.id;
		return sharedData?.shared_groups?.length > 0 || sharedData?.shared_users?.filter((u) => u.id !== currentUserId).length > 0;
	},
	[isWorkflowSharedWithOthers.getter]: (state, getters, rootState, rootGetters) => (wfId) => {
		const sharedData = getters[workflowSharedData.getter](wfId);
		if (!sharedData) {
			return undefined;
		}
		const currentUserId = rootGetters[userGetter.fullPath]?.id;
		return sharedData?.shared_groups?.length > 0 || sharedData?.shared_users?.filter((u) => u.id !== currentUserId).length > 0;
	},
	[isWaitingForSampleData.getter]: (state) => (subflowId) => state.waitingForSampleDataWorkflowIds.includes(Number(subflowId)),
	[updatingWorkflowIds.getter]: (state) => state.updatingWorkflowIds,
};

export default getters;
